import { ChangeDetectionStrategy, Component, inject, Signal, input, computed, InputSignal, DestroyRef, signal } from '@angular/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { ToastrService } from 'ngx-toastr';
import { OrderService, OrderControllerService, OrderUtilityService, UserService } from './../../../../services/';
import { ArcVersionMapping, OrderLine, OrderShipment, User, ShipmentLineReset } from './../../../../models/';
import { LineStatus, MediaType, OrderScreen, OrderType, ShipmentLineResetType } from '../../../../models/enums';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { OrderIssuesComponent } from '../../../../shared/components/order-issues/order-issues.component';
import { DatePipe } from '@angular/common';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { MatTooltipModule } from '@angular/material/tooltip';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatMenuModule } from '@angular/material/menu';
import { LoadingSpinnerComponent } from '../../../../shared/components/loading-spinner/loading-spinner.component';
import { CopyableDirective } from '../../../../shared/directives/copyable-directive';
import { OrderLineLanguagesComponent } from '../../../../shared/components/order-line-languages/order-line-languages.component';

@Component({
  selector: 'insight-order-shipment-details-grid',
  templateUrl: './order-shipment-details-grid.component.html',
  styleUrls: ['./order-shipment-details-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatButton,
    ScrollingModule,
    MatIcon,
    MatTooltipModule,
    OrderIssuesComponent,
    DatePipe,
    MatCheckboxModule,
    FormsModule,
    MatMenuModule,
    LoadingSpinnerComponent,
    CopyableDirective,
    OrderLineLanguagesComponent
  ],
  standalone: true
})
export class OrderShipmentDetailsGridComponent {
  public lineStatusEnums = LineStatus;
  public orderTypeEnum = OrderType;
  public mediaTypeEnum = MediaType;
  public ShipmentLineResetType = ShipmentLineResetType;

  public arcVersionMappingData: ArcVersionMapping[] = [];

  public orderShipment: InputSignal<OrderShipment> = input.required<OrderShipment>();
  public orderShipmentHasLineErrors = computed(() =>
    this.orderShipment().lines.some(l => l.errors.length > 0)
  );

  public resetRunning = signal(false);
  public orderScreen = OrderScreen;
  public lineStatuses = LineStatus;

  // Injected services
  private orderControllerService = inject(OrderControllerService);
  public orderUtilityService = inject(OrderUtilityService);
  private orderService = inject(OrderService);
  private userService = inject(UserService);
  private toastr = inject(ToastrService);

  public user: Signal<User> = toSignal(this.userService.getUser());
  public readonly isEditor = computed(() => { return this.user()?.permissions.insightEditor ?? false; });
  public readonly isOps = computed(() => { return this.user()?.permissions.insightOperations ?? false; });

  public destroyRef = inject(DestroyRef);

  private arcDescriptionSource: ArcVersionMapping[];
  private arcDescriptionByCode = new Map<string, string>();

  public convertArcCodeToDescription(version: string): string {
    // it is possible that ARC version mapping data is not loaded yet with the injection
    // on initial load. It eventually gets fixed, but can throw an error on page load
    const arcVersions = this.orderControllerService.getArcVersions();

    if (version === null || arcVersions === undefined) {
      return "Missing version"; // version not found
    }

    if (this.arcDescriptionSource !== arcVersions) {
      this.arcDescriptionSource = arcVersions;
      this.arcDescriptionByCode = new Map(arcVersions.map(arcDictValue =>
        [arcDictValue.tableCodeValue.trim().toLowerCase(), arcDictValue.codeDescription]));
    }

    return this.arcDescriptionByCode.get(version.trim().toLowerCase()) ?? "Missing version";
  }

  public linkToPubAdmin(productNumber: string) {
    this.orderControllerService.linkToPubAdmin(productNumber);
  }

  /**
   * Get the number of lines checked by the user for reset.
   */
  public get resetLineCount(): number {
    return this.orderShipment().lines.filter(line => line.resetSelected).length;
  }

  /**
   * If any lines are check for reset.
   */
  public canReset(): boolean {
    return this.resetLineCount > 0;
  }

  /**
   * Check if the line can be reset based on user permissions and  the
   * allowed resets for the line returned from the backend.
   */
  public canResetLine(line: OrderLine): boolean {
    return (this.isEditor() || this.isOps()) && line.allowedResets.length > 0;
  }

  /**
   * Check if any lines have valid resets.
   */
  public canAnyLinesBeSelected = computed(() =>
    this.orderShipment().lines.some(line => this.canResetLine(line))
  );

  /**
   * Check for the valid resets that can be applied to *all* of the selected lines.
   */
  public canResetLineWithResetType(resetType: ShipmentLineResetType): boolean {
    return this.orderShipment().lines
      .filter((line: OrderLine) => line.resetSelected)
      .every((line: OrderLine) => line.allowedResets.includes(resetType));
  }

  public resetLines(resetType: ShipmentLineResetType): void {
    this.resetRunning.set(true);
    let resetLines: ShipmentLineReset[] = this.orderShipment().lines
      .filter((line: OrderLine) => line.resetSelected)
      .map((line: OrderLine) => new ShipmentLineReset(line.lineNumber, resetType));

    this.orderService.resetShipmentLines(resetLines, this.orderShipment().orderNumber, this.orderShipment().shipmentNumber, this.orderShipment().sourceSystem)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.resetRunning.set(false);
          this.orderControllerService.newOrderDetailsAvailableEvent.next();
        },
        error: (error) => {
          this.resetRunning.set(false);
          this.toastr.error(`Failed to reset order line(s): ${error?.error}`, 'Error');
        }
      });
  }

  public selectResets(selected: boolean): void {
    this.orderShipment().lines.forEach(line => {
      if (this.canResetLine(line)) {
        line.resetSelected = selected;
      }
    });
  }

  public readonly isAnyCompleteOrder = computed(() => {
    return this.isCompleteAdebsOrder() || this.isCompleteStreamingOrder() || this.isCompleteCinelinkOrder();
  });

  public readonly isAllLicensesIdentical = computed(() => {
    return this.orderUtilityService.isAllLicensesSameDateRange(this.orderShipment().lines);
  });

  public readonly isCompleteAdebsOrder = computed(() => {
    const validMediaTypes = [MediaType.HardDrive, MediaType.Cvd, MediaType.NetworkDelivery];
    return this.isCompletedOrder() &&
      this.orderShipment().orderType === OrderType.Adebs &&
      validMediaTypes.includes(this.orderShipment().mediaType);
  });

  private readonly isCompleteStreamingOrder = computed(() => {
    return this.isCompletedOrder() && this.orderShipment().orderType === OrderType.StreamingServer;
  });

  private readonly isCompleteCinelinkOrder = computed(() => {
    return this.isCompletedOrder() && this.orderShipment().orderType === OrderType.Cinelink;
  });

  private readonly isCompletedOrder = computed(() => {
    return this.orderShipment().lines.every(l =>
      [LineStatus.Complete, LineStatus.Cancelled, LineStatus.Shipped].includes(l.status)
    );
  });
}
